import { useCallback, useEffect, useMemo, useState } from "react";
import { FlaskConical, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Page";
import VipPanel from "@/components/VipPanel";
import GoldStore from "@/components/GoldStore";
import SlotStore from "@/components/SlotStore";
import ContactSupport from "@/components/ContactSupport";
import PromoCodeRedemption from "@/components/PromoCodeRedemption";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currencyGainToast } from "@/lib/currencyToastGate";

// Custom VIP Icon for PageHeader
const VipIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/vip-icon.png" 
    alt="VIP Membership" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

export default function Vip() {
  const [playTest, setPlayTest] = useState(false);

  useEffect(() => { api.get("/play-test").then((r) => setPlayTest(!!r.data.active)).catch(() => {}); }, []);

  return (
    <div>
      <PageHeader title="VIP Membership" subtitle="Subscribe to help keep the game alive and receive some extra in-game benefits!" icon={VipIcon} />
      {playTest && (
        <div data-testid="vip-play-test-banner" className="mb-6 flex items-center gap-3 border border-sky-500/60 bg-sky-950/25 px-4 py-3 rounded-sm">
          <FlaskConical size={18} className="text-sky-400 shrink-0" />
          <p className="text-sm text-sky-100">
            <span className="font-cinzel uppercase tracking-wide text-sky-300">Game is currently in testing</span>{" "}
            but VIP is live. You can still purchase and use VIP perks if you&apos;d like.
          </p>
        </div>
      )}
      <VipPanel />

      <GoldStore />

      <SlotStore />

      <ContactSupport />

      <PromoCodeRedemption />

      <VipVoteSection />
    </div>
  );
}

function VipVoteSection() {
  const { user } = useAuth();
  const [voteReward, setVoteReward] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());

  const loadVoteReward = useCallback(async () => {
    try {
      const { data } = await api.get("/vip/vote-reward/status");
      setVoteReward(data);
    } catch {
      setVoteReward(null);
    }
  }, []);

  useEffect(() => { loadVoteReward().catch(() => {}); }, [loadVoteReward]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const siteStates = voteReward?.sites || {};

  const handleVoteClick = useCallback(async (site) => {
    try {
      const { data } = await api.post("/vip/vote-reward/claim", { site_id: site.id });
      setVoteReward(data.status);
      if (data.awarded) {
        currencyGainToast(data.message);
      } else {
        const siteState = data.status?.sites?.[site.id];
        if (siteState?.next_claim_at) {
          toast(`Vote reward already claimed. Try again in ${formatCountdown(siteState.seconds_until_next_claim)}.`);
        }
      }
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, []);

  return (
    <section data-testid="help-us-section" className="mt-10 border border-amber-900/30 bg-zinc-950 p-6">
      <div className="flex items-center gap-2 mb-1">
        <HeartHandshake size={18} className="text-amber-400" />
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg">Help Us!</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        Help more lanistae find the arena — cast a vote for Lanista Manager on these game listing sites. It&apos;s free and takes a second!
      </p>
      <p data-testid="help-us-vote-reward-note" className="text-sm text-amber-200 mb-1">
        You are awarded 2 gold per vote per 24 hours, per site.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {VOTE_SITES.map((s) => {
          const siteState = siteStates[s.id];
          const cooldownSeconds = siteState?.next_claim_at
            ? Math.max(0, Math.ceil((new Date(siteState.next_claim_at).getTime() - nowMs) / 1000))
            : 0;
          const lastClaimLine = siteState?.last_claim_at
            ? `Last claimed ${new Date(siteState.last_claim_at).toLocaleString()}`
            : "Not claimed yet";
          return (
            <a
              key={s.id}
              href={s.href}
              title={s.title}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`vote-${s.id}`}
              onClick={() => { if (user) void handleVoteClick(s); }}
              className="border border-amber-900/30 bg-zinc-900/60 hover:border-amber-700/50 hover:bg-zinc-900 transition-colors p-4 min-h-[168px] flex flex-col justify-between"
            >
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-amber-400">Vote Site</div>
                <h3 className="font-cinzel text-lg text-zinc-100 mt-2" data-testid={`vote-title-${s.id}`}>{s.name}</h3>
                <p className="text-sm text-zinc-400 mt-2">{s.description}</p>
                <p data-testid={`vote-timer-${s.id}`} className="mt-4 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  {siteState?.can_claim || !siteState
                    ? `Reward ready · +${voteReward?.amount || 2} gold`
                    : `Reward in ${formatCountdown(cooldownSeconds)}`}
                </p>
                <p data-testid={`vote-last-claimed-${s.id}`} className="mt-2 text-[11px] text-zinc-600">
                  {lastClaimLine}
                </p>
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-4">Opens in a new tab</div>
            </a>
          );
        })}
      </div>
    </section>
  );
}



const VOTE_SITES = [
  {
    id: "top100arena",
    href: "https://www.top100arena.com/category/general?vote=102435",
    title: "Vote on Top100Arena",
    name: "Top100Arena",
    description: "Support Lanista on the arena browser-game rankings.",
  },
  {
    id: "browsergamestop",
    href: "https://browsergamestop.com/vote/lanista-online-gladiator-manager",
    title: "Vote on BrowserGamesTop",
    name: "BrowserGamesTop",
    description: "Give the arena a lift on BrowserGamesTop.",
  },
  {
    id: "browsermmorpg",
    href: "https://browsermmorpg.com/game-lanista---gladiator-manager--1756",
    title: "Vote on BrowserMMORPG",
    name: "BrowserMMORPG",
    description: "Help Lanista climb the BrowserMMORPG listings.",
  },
  {
    id: "votekicker",
    href: "https://votekicker.com/embed/lanista-online-gladiator-manager?theme=dark",
    title: "Vote on VoteKicker",
    name: "VoteKicker",
    description: "Cast a support click on VoteKicker once each day.",
  },
];

function formatCountdown(totalSeconds) {
  const seconds = Math.max(0, totalSeconds || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours <= 0 && minutes <= 0) return `${secs}s`;
  if (hours <= 0) return `${minutes}m ${secs}s`;
  if (minutes <= 0) return `${hours}h ${secs}s`;
  return `${hours}h ${minutes}m`;
}
